import { useState } from "react";
import { SectionCard } from "shared/app/SectionCard";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { useFormTyped, type Register } from "shared/hooks";
import { Row } from "views/applications-id-report/components/Row";
import { AmenitiesForm } from "shared/app/AmenitiesForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { PropertyKind } from "shared/types";
import { Property } from "types/landlord/address";
import { useAuthContext } from "providers/Auth";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { AmenitiesSelect } from "shared/app/AmenitiesForm/components/AmenitiesSelect";
import { PetPolicyPmsField } from "shared/app/AmenitiesForm/components/PetPolicyPmsField";

const getYesNo = (value: boolean) => (value ? "Yes" : "No");

const AmenitiesList = ({ amenities }: { amenities: Property["amenities"] }) => {
  if (!amenities.length)
    return <p className="font-medium text-content-1000">-</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {amenities.map((amenity) => (
        <Badge key={amenity.id} variant={BadgeVariant.BASE} size={BadgeSize.SM}>
          {amenity.title}
        </Badge>
      ))}
    </div>
  );
};

interface EditAmenitiesFormProps {
  onClose: () => void;
  initialValues: {
    dogs_allowed: boolean;
    cats_allowed: boolean;
    amenities: number[];
  };
  propertyId: number;
  level: "property" | "unit";
}

const EditAmenitiesForm = ({
  onClose,
  initialValues,
  propertyId,
  level
}: EditAmenitiesFormProps) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);

  const { mutate: updateAmenities, isPending } = useMutation({
    mutationFn: () => {
      return addressApi.editProperty({
        propertyId,
        pet_policy: {
          cats_allowed: values.cats_allowed,
          dogs_allowed: values.dogs_allowed
        },
        amenity_ids: values.amenities
      });
    },
    onSuccess: () => {
      toast.success("Amenities updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update amenities: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => {
      updateAmenities();
    }
  });

  return (
    <AmenitiesForm
      level={level}
      register={register as unknown as Register}
      selectedAmenities={values.amenities}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    />
  );
};

interface PmsEditAmenitiesFormValues {
  dogs_allowed: boolean;
  cats_allowed: boolean;
  amenities: number[];
  pms_overrides: {
    cats_allowed?: boolean;
    dogs_allowed?: boolean;
    amenities?: boolean;
  };
}

const PmsEditAmenitiesForm = ({
  onClose,
  initialValues,
  propertyId,
  level,
  property
}: {
  onClose: () => void;
  initialValues: PmsEditAmenitiesFormValues;
  propertyId: number;
  level: "property" | "unit";
  property: Property;
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);
  const [petPolicyTouched, setPetPolicyTouched] = useState<{
    cats_allowed: boolean;
    dogs_allowed: boolean;
  }>({ cats_allowed: false, dogs_allowed: false });
  const overrides = values.pms_overrides;

  const { mutate: updateAmenities, isPending } = useMutation({
    mutationFn: () => {
      const petPolicy = {
        ...(petPolicyTouched.cats_allowed && {
          cats_allowed: values.cats_allowed
        }),
        ...(petPolicyTouched.dogs_allowed && {
          dogs_allowed: values.dogs_allowed
        })
      };
      return addressApi.editProperty({
        propertyId,
        ...(Object.keys(petPolicy).length > 0 && { pet_policy: petPolicy }),
        amenity_ids: values.amenities,
        pms_overrides: {
          ...property.pms_overrides,
          ...values.pms_overrides
        }
      });
    },
    onSuccess: () => {
      toast.success("Amenities updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update amenities: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => updateAmenities()
  });

  const handlePetPolicyChange = (
    field: "cats_allowed" | "dogs_allowed",
    value: unknown
  ) => {
    setPetPolicyTouched((prev) => ({ ...prev, [field]: true }));
    register(field).onChange(value);
  };

  const handleOverrideChange = (
    field: "cats_allowed" | "dogs_allowed",
    synced: boolean
  ) => {
    const newOverrides = { ...overrides, [field]: synced };
    register("pms_overrides").onChange(newOverrides);

    setPetPolicyTouched((prev) => ({ ...prev, [field]: true }));

    if (synced) {
      const pmsVal = property.pms_values?.[field];
      register(field).onChange(pmsVal != null ? pmsVal : initialValues[field]);
    }
  };

  return (
    <SectionCard
      title="Amenities"
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    >
      <div className="space-y-8">
        <PetPolicyPmsField
          id="cat-allowed"
          label="Cat allowed"
          radioName="property_cats_allowed"
          selectProps={register("cats_allowed")}
          synced={overrides?.cats_allowed ?? true}
          pmsValue={property.pms_values?.cats_allowed}
          onValueChange={(val) => handlePetPolicyChange("cats_allowed", val)}
          onSyncChange={(val) => handleOverrideChange("cats_allowed", val)}
        />

        <PetPolicyPmsField
          id="dog-allowed"
          label="Dog allowed"
          radioName="property_dogs_allowed"
          selectProps={register("dogs_allowed")}
          synced={overrides?.dogs_allowed ?? true}
          pmsValue={property.pms_values?.dogs_allowed}
          onValueChange={(val) => handlePetPolicyChange("dogs_allowed", val)}
          onSyncChange={(val) => handleOverrideChange("dogs_allowed", val)}
        />

        <div className="grid grid-cols-2 gap-4 items-start">
          <div>
            <AmenitiesSelect
              level={level}
              onSelect={(value) => register("amenities").onChange(value)}
              selectedAmenities={values.amenities}
              disabled={overrides?.amenities ?? true}
            />
            <PmsSyncOption
              value={overrides?.amenities ?? true}
              onChange={(val) => {
                register("pms_overrides").onChange({
                  ...overrides,
                  amenities: val
                });
                // When switching ON, mirror the PMS list — including the
                // empty case — so the form doesn't keep a stale manual entry.
                if (val)
                  register("amenities").onChange(
                    property.pms_values?.amenity_ids ?? []
                  );
              }}
              name="property_amenities"
            />
          </div>
          <PMSValue
            value={
              property.pms_values?.amenities?.length
                ? property.pms_values.amenities.join(", ")
                : undefined
            }
          />
        </div>
      </div>
    </SectionCard>
  );
};

interface AmenitiesSectionProps {
  amenities: Property["amenities"];
  propertyId: number;
  petPolicy?: {
    dogs_allowed?: boolean;
    cats_allowed?: boolean;
  };
  propertyKind: PropertyKind | null;
  property: Property;
}

export const AmenitiesSection = ({
  amenities,
  propertyId,
  petPolicy,
  propertyKind = PropertyKind.MultiFamily,
  property
}: AmenitiesSectionProps) => {
  const [isEditting, setIsEditting] = useState(false);
  const { type } = useAuthContext();
  const isPmsProperty = type.isPmsListingSyncEnabled && !!property.pms_id;
  const level =
    propertyKind === PropertyKind.SingleFamily ? "unit" : "property";

  const resolvedCatsAllowed = Boolean(petPolicy?.cats_allowed);
  const resolvedDogsAllowed = Boolean(petPolicy?.dogs_allowed);

  if (isEditting) {
    if (isPmsProperty)
      return (
        <PmsEditAmenitiesForm
          level={level}
          onClose={() => setIsEditting(false)}
          initialValues={{
            dogs_allowed: resolvedDogsAllowed,
            cats_allowed: resolvedCatsAllowed,
            // When sync is on, mirror the PMS list (even when empty) so the
            // form doesn't show a stale manual amenity that contradicts
            // PMS Value: N/A.
            amenities: property.pms_overrides?.amenities
              ? (property.pms_values?.amenity_ids ?? [])
              : amenities.map((amenity) => amenity.id),
            pms_overrides: {
              cats_allowed: property.pms_overrides?.cats_allowed ?? true,
              dogs_allowed: property.pms_overrides?.dogs_allowed ?? true,
              amenities: property.pms_overrides?.amenities ?? true
            }
          }}
          propertyId={propertyId}
          property={property}
        />
      );

    return (
      <EditAmenitiesForm
        level={level}
        onClose={() => setIsEditting(false)}
        initialValues={{
          dogs_allowed: Boolean(petPolicy?.dogs_allowed),
          cats_allowed: Boolean(petPolicy?.cats_allowed),
          amenities: amenities.map((amenity) => amenity.id)
        }}
        propertyId={propertyId}
      />
    );
  }

  return (
    <SectionCard
      title="Amenities"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditting(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4 w-full">
        <Row title="Cats allowed" value={getYesNo(resolvedCatsAllowed)} />
        <Row title="Dogs allowed" value={getYesNo(resolvedDogsAllowed)} />
        <Row
          title="Amenities"
          value={<AmenitiesList amenities={amenities} />}
          valueClassName="w-[500px]"
        />
      </div>
    </SectionCard>
  );
};
